import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { FluidModule } from 'primeng/fluid';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PopoverModule } from 'primeng/popover';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';

// Application imports
import { ScenarioViewModel } from '../viewmodels/scenario-viewmodel';
import { ScenarioService } from '../services/scenario.service';
import { ScenarioExportService } from '../services/scenario-export.service';
import { MessageHelperService } from '../../../base-classes/master-message-service';
import { ToastService } from 'src/app/toast/toast.service';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { StepsService } from '../../steps/services/steps.service';
import { AuthService } from '../../../auth/login/services/auth.service';
import { AuthProfileService } from '../../auth-profile/services/auth-profile.service';
import { ScreenMasterService } from '../../screen-master/services/screen-master.service';

@Component({
    selector: 'app-scenario',
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        FormsModule,
        TableModule,
        ButtonModule,
        DialogModule,
        InputTextModule,
        TextareaModule,
        SelectModule,
        MultiSelectModule,
        ToastModule,
        ConfirmDialogModule,
        TooltipModule,
        FluidModule,
        IconFieldModule,
        InputIconModule,
        PopoverModule,
        AccordionModule,
        CheckboxModule
    ],
    providers: [
        ConfirmationService
    ],
    templateUrl: './scenario.component.html',
    styleUrl: './scenario.component.scss'
})
export class ScenarioComponent implements OnInit, OnDestroy, AfterViewChecked {
  viewModel: ScenarioViewModel;
  @ViewChild('faTabsContainer') faTabsContainer?: ElementRef<HTMLElement>;
  @ViewChild('faTabsMeasure') faTabsMeasure?: ElementRef<HTMLElement>;
  private resizeObs?: ResizeObserver;
  private lastContainerWidth = 0;
  private lastTabCount = 0;

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private scenarioService: ScenarioService,
    private exportService: ScenarioExportService,
    private router: Router,
    private route: ActivatedRoute,
    private functionalAreaService: FuntionalAreaService,
    private stepsService: StepsService,
    private authService: AuthService,
    private authProfileService: AuthProfileService,
    private screenMasterService: ScreenMasterService,
    private zone: NgZone
  ) {
    this.viewModel = new ScenarioViewModel(
      this.http,
      this.toastService,
      this.confirmationService,
      this.scenarioService,
      this.exportService,
      this.router,
      this.route,
      this.functionalAreaService,
      this.stepsService,
      this.authService,
      this.authProfileService,
      this.screenMasterService
    );
  }

  ngOnInit(): void {
    this.viewModel.init();
  }

  ngAfterViewChecked(): void {
    if (!this.faTabsContainer || !this.faTabsMeasure) return;
    const container = this.faTabsContainer.nativeElement;
    const measure = this.faTabsMeasure.nativeElement;
    const width = container.clientWidth;
    const count = this.viewModel.faTabCounts().length;
    if (width === this.lastContainerWidth && count === this.lastTabCount) return;
    this.lastContainerWidth = width;
    this.lastTabCount = count;
    this.recomputeVisibleTabs(measure, width);
    if (!this.resizeObs && typeof ResizeObserver !== 'undefined') {
      this.zone.runOutsideAngular(() => {
        this.resizeObs = new ResizeObserver(() => {
          this.zone.run(() => {
            const w = container.clientWidth;
            if (w !== this.lastContainerWidth) {
              this.lastContainerWidth = w;
              this.recomputeVisibleTabs(measure, w);
            }
          });
        });
        this.resizeObs.observe(container);
      });
    }
  }

  private recomputeVisibleTabs(measure: HTMLElement, containerWidth: number): void {
    const buttons = Array.from(measure.querySelectorAll('button')) as HTMLElement[];
    if (buttons.length < 2) return;
    const moreBtn = buttons[buttons.length - 1];
    const tabBtns = buttons.slice(0, -1);
    const allTabWidth = tabBtns[0]?.offsetWidth || 0;
    const moreWidth = moreBtn.offsetWidth + 8;
    let used = allTabWidth;
    let limit = 0;
    for (let i = 1; i < tabBtns.length; i++) {
      const next = tabBtns[i].offsetWidth + 2;
      const remaining = tabBtns.length - 1 - i;
      const reserveMore = remaining > 0 ? moreWidth : 0;
      if (used + next + reserveMore > containerWidth) break;
      used += next;
      limit++;
    }
    if (limit !== this.viewModel.visibleTabLimit) {
      this.viewModel.visibleTabLimit = limit;
    }
  }

  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
    this.viewModel.destroy();
  }
}